import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, GitMerge, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function MergeDialog({ value, onClose }) {
  const qc = useQueryClient();
  const [targetId, setTargetId] = useState("");

  const { data: targets = [] } = useQuery({
    queryKey: ["merge-targets", value.list_type],
    queryFn: async () =>
      (await api.get(`/master-data/${value.list_type}`, {
        params: { include_inactive: false, include_pending: false },
      })).data,
  });

  const { data: preview } = useQuery({
    queryKey: ["merge-preview", value.id, targetId],
    queryFn: async () =>
      (await api.get(`/admin/cleanup-queue/${value.id}/merge-preview`, {
        params: { target_id: targetId },
      })).data,
    enabled: !!targetId,
  });

  const doMerge = useMutation({
    mutationFn: async () =>
      api.post(`/admin/cleanup-queue/${value.id}/merge`, { target_id: targetId }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["cleanup-queue"] });
      toast.success(`Merged — ${res.data.reassigned} record(s) reassigned`);
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Merge failed"),
  });

  const options = targets.filter((t) => t.id !== value.id);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle>Merge "{value.label}" into…</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            All records pointing at <strong>{value.label}</strong> will be reassigned to the
            surviving value. "{value.label}" is then deactivated (kept for history).
          </p>
          <div>
            <Label>Surviving value</Label>
            <Select value={targetId} onValueChange={setTargetId}>
              <SelectTrigger className="mt-1" data-testid="merge-target-select">
                <SelectValue placeholder="Select target value…" />
              </SelectTrigger>
              <SelectContent>
                {options.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {preview && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm" data-testid="merge-preview">
              <div className="font-semibold text-slate-900">{preview.affected_records} record(s) will be reassigned</div>
              {Object.entries(preview.breakdown || {}).filter(([, n]) => n > 0).map(([k, n]) => (
                <div key={k} className="text-xs text-slate-500 mt-0.5">{k}: {n}</div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => doMerge.mutate()}
            disabled={!targetId || doMerge.isPending}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="confirm-merge-btn"
          >
            <GitMerge className="h-4 w-4 mr-1.5" /> Merge & deactivate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TaxonomyCleanup() {
  const qc = useQueryClient();
  const [mergeFor, setMergeFor] = useState(null);

  const { data: queue = [], isLoading } = useQuery({
    queryKey: ["cleanup-queue"],
    queryFn: async () => (await api.get("/admin/cleanup-queue")).data,
  });

  const approve = useMutation({
    mutationFn: async (id) => api.post(`/admin/cleanup-queue/${id}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cleanup-queue"] });
      toast.success("Approved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const reject = useMutation({
    mutationFn: async (id) => api.post(`/admin/cleanup-queue/${id}/reject`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cleanup-queue"] });
      toast.success("Rejected & deactivated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <div className="p-8 max-w-4xl" data-testid="taxonomy-cleanup-page">
      <div className="label-eyebrow">Admin · Taxonomy</div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Cleanup Queue</h1>
      <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
        New taxonomy values added inline by your team land here for review. Approve to keep,
        Merge into an existing value to de-duplicate, or Reject to deactivate.
      </p>

      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className="label-eyebrow !text-slate-600">Value</th>
              <th className="label-eyebrow !text-slate-600">List</th>
              <th className="label-eyebrow !text-slate-600">Added by</th>
              <th className="label-eyebrow !text-slate-600 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="text-center py-6 text-sm text-slate-500">Loading…</td></tr>
            ) : queue.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-10 text-sm text-slate-500" data-testid="cleanup-empty">Nothing pending review. 🎉</td></tr>
            ) : queue.map((v) => (
              <tr key={v.id} className="border-t border-slate-100" data-testid={`cleanup-row-${v.id}`}>
                <td className="text-sm font-semibold text-slate-900">{v.label}</td>
                <td>
                  <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-600 font-mono-num">
                    {v.list_type}
                  </Badge>
                </td>
                <td className="text-sm text-slate-600">{v.created_by_name || "—"}</td>
                <td>
                  <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => approve.mutate(v.id)} data-testid={`cleanup-approve-${v.id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setMergeFor(v)} data-testid={`cleanup-merge-${v.id}`}>
                      <GitMerge className="h-3.5 w-3.5 mr-1" /> Merge
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => reject.mutate(v.id)} data-testid={`cleanup-reject-${v.id}`}>
                      <X className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {mergeFor && <MergeDialog value={mergeFor} onClose={() => setMergeFor(null)} />}
    </div>
  );
}
